import type { ErrorResponseDto, SuccessResponseDto } from '@splitfiesta/shared';

/**
 * Extract data from API response with type safety
 * @param response - The raw API response
 * @returns The extracted data with proper typing
 */
export function extractData<T>(response: unknown): T {
  if (response && typeof response === 'object' && 'data' in response) {
    return (response as { data: T }).data;
  }
  throw new Error('Invalid API response structure');
}

/**
 * Type guard to check if response is a success response
 * @param response - The response to check
 * @returns True if response is a SuccessResponseDto
 */
export function isSuccessResponse<T>(
  response: unknown
): response is SuccessResponseDto<T> {
  return (
    response !== null &&
    typeof response === 'object' &&
    'success' in response &&
    (response as { success: unknown }).success === true &&
    'data' in response
  );
}

/**
 * Type guard to check if response is an error response
 * @param response - The response to check
 * @returns True if response is an ErrorResponseDto
 */
export function isErrorResponse(
  response: unknown
): response is ErrorResponseDto {
  return (
    response !== null &&
    typeof response === 'object' &&
    'error' in response &&
    'message' in response &&
    'status' in response
  );
}

/**
 * Extract paginated data from API response
 * @param response - The raw API response
 * @returns The paginated data with metadata
 */
export function extractPaginatedData<T>(response: unknown): {
  data: T[];
  hasMore: boolean;
  nextCursor?: string;
  total?: number;
} {
  if (response && typeof response === 'object' && 'data' in response) {
    const responseData = (response as { data: unknown }).data;

    // Handle direct array response (legacy)
    if (Array.isArray(responseData)) {
      return {
        data: responseData as T[],
        hasMore: false,
      };
    }

    // Handle paginated response structure
    if (responseData && typeof responseData === 'object') {
      const paginatedResponse = responseData as Record<string, unknown>;

      // Check for different pagination structures
      if ('items' in paginatedResponse) {
        return {
          data: paginatedResponse.items as T[],
          hasMore: (paginatedResponse.hasMore ?? false) as boolean,
          nextCursor: paginatedResponse.nextCursor as string | undefined,
          total: paginatedResponse.total as number | undefined,
        };
      }

      // Check for direct properties (e.g., expenses, groups)
      const dataKey = Object.keys(paginatedResponse).find(key =>
        Array.isArray(paginatedResponse[key])
      );

      if (dataKey) {
        return {
          data: paginatedResponse[dataKey] as T[],
          hasMore: (paginatedResponse.hasMore ?? false) as boolean,
          nextCursor: paginatedResponse.nextCursor as string | undefined,
          total: paginatedResponse.total as number | undefined,
        };
      }
    }
  }

  throw new Error('Invalid paginated response structure');
}

/**
 * Safe type assertion for API responses
 * @param response - The response to assert
 * @returns The typed response
 */
export function assertApiResponse<T>(response: unknown): T {
  if (!response) {
    throw new Error('Empty API response');
  }
  return response as T;
}
